import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { environment } from '../../../environments/environment';
import { filter } from 'rxjs/internal/operators/filter';
import {
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { CONSTANTS_DETAILS } from '../../shared';
import { HttpClient } from '@angular/common/http';
import { SnackbarService } from '../snackbar/snackbar.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public readonly _destroying$ = new Subject<void>();
  public userRoles = new BehaviorSubject<string[]>([]);
  public hasRoles = true;
  constructor(
    private _authService: MsalService,
    private _msalBroadcastService: MsalBroadcastService,
    private _router: Router,
    private _http: HttpClient,
    private _snackbarService: SnackbarService,
    private _translate: TranslateService,
  ) {}

  /**
   * Initializes the authentication process by enabling account storage events
   *
   * @memberof AuthService
   */
  public authInitiator(): void {
    this._authService.instance.enableAccountStorageEvents();
    // Subscribe to the MSAL broadcast service to listen for account changes
    // This will handle events like account added, removed, login success, and login failure
    this._msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.LOGIN_FAILURE,
        ),
      )
      .subscribe((result: EventMessage) => {
        if (result.eventType === EventType.LOGIN_SUCCESS) {
          this._router.navigate(['/home']);
        }
      });
    // Subscribe to the broadcast service to listen for authentication status changes
    this._msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.isAuthenticated();
        this.getAndUpdateCurrentUserAccount();
      });
  }

  /**
   * Checks if the user is authenticated.
   *
   * @returns {boolean} True if the user is authenticated, otherwise false.
   * @memberof AuthService
   */
  public isAuthenticated(): boolean {
    return this._authService.instance.getAllAccounts().length > 0;
  }

  /**
   * Gets the set current user account information.
   *
   * @returns {void} The current user account information or null if not authenticated.
   * @memberof AuthService
   */
  public getAndUpdateCurrentUserAccount(): void {
    if (this.isAuthenticated()) {
      const activeAccount = this._authService.instance.getActiveAccount();
      if (
        !activeAccount &&
        this._authService.instance.getAllAccounts().length > 0
      ) {
        const accounts = this._authService.instance.getAllAccounts();
        this._authService.instance.setActiveAccount(accounts[0]);
      }
      this.getRolesPrivileges().then((response) => {
        if (response) {
          this.userRoles.next(response as string[]);
        }
      });
      this._router.navigate(['/home']);
    } else {
      this.loginUser();
    }
  }

  /**
   * Logs in the user using a redirect method.
   *
   * @return {*}  {void}
   * @memberof AuthService
   */
  public loginUser(): void {
    this._authService.loginRedirect({
      scopes: environment.AUTH_CONFIG.dut_scope,
    });
  }

  /**
   * Logs out the user and redirects to the post-logout URI.
   *
   * @memberof AuthService
   */
  public logoutUser(): void {
    this._authService.logoutRedirect({
      postLogoutRedirectUri: environment.AUTH_CONFIG.postLogoutRedirectUri,
      account: this._authService.instance.getActiveAccount(),
    });
  }

  /**
   * Gets the user's email address from the current account.
   *
   * @return {*}  {string[]}
   * @memberof AuthService
   */
  public getAccountGroups() {
    return this._authService.instance.getActiveAccount()
      ? this._authService.instance.getActiveAccount()
      : null;
  }

  /**
   * Fetches the roles and privileges of the user from the server.
   *
   * @return {*}
   * @memberof AuthService
   */
  public getRolesPrivileges() {
    return lastValueFrom(
      this._http.get(
        environment.APP_BASE_URL + CONSTANTS_DETAILS.GET_USERPRIVILEGES_URL,
      ),
    ).catch(() => {
      this._snackbarService.open({
        message: this._translate.instant('errorFetchinguserRoles'),
        type: 'error',
      });
      return [];
    });
  }
}
